import { useEffect, useState } from "react";
import { Cookie, X, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "scopelink_cookie_consent_v1";

export default function CookieBanner({ lang }) {
  const [visible, setVisible] = useState(false);
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    let saved = null;
    try {
      saved = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      saved = null;
    }
    if (saved) return undefined;
    // Slight delay so the banner doesn't compete with the hero entrance
    const id = window.setTimeout(() => setVisible(true), 700);
    return () => window.clearTimeout(id);
  }, []);

  const persist = (value) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, ts: new Date().toISOString() })
      );
    } catch (e) {
      /* noop */
    }
    setHiding(true);
    window.setTimeout(() => setVisible(false), 350);
  };

  if (!visible) return null;

  const copy =
    lang === "ES"
      ? {
          title: "Cookies en órbita",
          body:
            "Usamos cookies propias y de terceros para analíticas anónimas y mejorar tu experiencia en scopelink.io. Puedes aceptarlas, rechazarlas o consultar nuestra política de cookies.",
          accept: "Aceptar",
          reject: "Rechazar",
          policy: "Política de cookies",
          close: "Cerrar",
        }
      : {
          title: "Cookies in orbit",
          body:
            "We use first- and third-party cookies for anonymous analytics and to improve your experience on scopelink.io. You can accept them, reject them or read our cookie policy.",
          accept: "Accept",
          reject: "Reject",
          policy: "Cookie policy",
          close: "Close",
        };

  return (
    <div
      data-testid="cookie-banner"
      className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 z-[60] md:max-w-md transition-all duration-300 ${
        hiding ? "opacity-0 translate-y-3" : "opacity-100 translate-y-0"
      }`}
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="relative border border-white/15 bg-[#0b101e]/95 backdrop-blur-xl p-5 md:p-6 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]">
        {/* Crosshair accents */}
        <span
          aria-hidden="true"
          className="absolute -top-px -left-px h-3 w-3 border-t border-l border-[var(--primary)]"
        />
        <span
          aria-hidden="true"
          className="absolute -bottom-px -right-px h-3 w-3 border-b border-r border-[var(--primary)]"
        />

        {/* Close */}
        <button
          data-testid="cookie-close"
          onClick={() => persist("dismissed")}
          aria-label={copy.close}
          className="absolute top-3 right-3 inline-flex h-7 w-7 items-center justify-center text-white/40 hover:text-white transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <span className="inline-flex h-9 w-9 items-center justify-center border border-[var(--primary)]/40 bg-[var(--primary)]/5 flex-shrink-0">
            <Cookie className="h-4 w-4 text-[var(--primary)]" strokeWidth={1.5} />
          </span>
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)]">
              // GDPR · ePrivacy
            </div>
            <h3 className="font-display text-base font-bold text-white mt-1">
              {copy.title}
            </h3>
          </div>
        </div>

        <p className="text-white/65 text-[13px] leading-relaxed mb-5">
          {copy.body}{" "}
          <Link
            to={lang === "ES" ? "/politica-de-cookies" : "/cookie-policy"}
            data-testid="cookie-policy-link"
            className="inline-flex items-center gap-1 text-[var(--primary)] hover:underline underline-offset-4"
          >
            <ShieldCheck className="h-3 w-3" />
            {copy.policy}
          </Link>
        </p>

        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="cookie-reject"
            onClick={() => persist("rejected")}
            className="border border-white/20 text-white font-mono text-[11px] tracking-[0.25em] uppercase font-semibold py-3 hover:border-white/50 hover:text-white transition-colors"
          >
            {copy.reject}
          </button>
          <button
            data-testid="cookie-accept"
            onClick={() => persist("accepted")}
            className="bg-[var(--primary)] text-black font-mono text-[11px] tracking-[0.25em] uppercase font-semibold py-3 hover:bg-[var(--primary-hover)] transition-colors"
          >
            {copy.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
